import { Checkbox, Typography } from "antd";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "../../../assets/css/setting.css";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HTextArea from "../../../components/custom/input/textarea/HTextArea";
import { AppContext } from "../../../context/AppContext";
import EmailComponent from "../../Setting/EmailComponent";
import type { WorkflowEnumItem } from "../hooks/useWorkflowEnums";
import type { ActionNodeData } from "../nodes/ActionNode";
import { useWorkflowStore } from "../store";
import { Trigger } from "../nodes/TriggerNode";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";
import { getActionIconAndClass, getFormTypeForAction } from "../utils/actionCodeMapping";
import { ACTION_HELP_CONTENT } from "../utils/helpContentConfig";
import TimeSelector from "./TimeSelector";
import { AutomatedResponseForm } from "./AutomatedResponseForm";
import { ConfigPanelFooter } from "./ConfigPanelFooter";
import { ConfigPanelShell } from "./ConfigPanelShell";
import { StepTypePicker } from "./StepTypePicker";
import { useStepConfigPanel } from "../hooks/useStepConfigPanel";

const { Text } = Typography;

export interface ActionConfigPanelProps {
    actionTypes: WorkflowEnumItem[];
}

const DEFAULT_EMAIL_NOTIFICATION_MESSAGE =
    "You have received a new notification. Please check your account for details.";

const NotifyUserForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => {
    const [appProperties] = useContext(AppContext);
    const [emailValidationErrors, setEmailValidationErrors] = useState<Record<string, string> | null>(null);

    const emailFieldConfig = useMemo(
        () => ({
            key: "additionalEmails",
            placeholder: "Enter an email address",
            onchange: (_parentKey: string, _key: string, value: string[]) => {
                update({ additionalEmails: value });
            },
        }),
        [update],
    );

    React.useEffect(() => {
        if (!data.notificationTypes?.includes("Email")) {
            update({ notificationTypes: ["Email"] });
        }
    }, [data.notificationTypes, update]);

    React.useEffect(() => {
        if (data.notifyUser != null && data.recordOwner == null && data.accountOwner == null) {
            const legacyNotifyUser = data.notifyUser;
            update({
                recordOwner: legacyNotifyUser === "Record Owner",
                accountOwner: legacyNotifyUser === "Account Owner",
                includeAdditionalEmails: legacyNotifyUser === "Additional Email Addresses",
                additionalEmails: legacyNotifyUser === "Additional Email Addresses" ? data.additionalEmails ?? [] : [],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time migration from legacy notifyUser
    }, [data.notifyUser, data.recordOwner, data.accountOwner]);

    const handleRecordOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ recordOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAccountOwnerChange = (e: { target: { checked: boolean } }) => {
        update({ accountOwner: e.target.checked });
        setEmailValidationErrors(null);
    };
    const handleAdditionalEmailsChange = (e: { target: { checked: boolean } }) => {
        update({
            includeAdditionalEmails: e.target.checked,
            additionalEmails: e.target.checked ? data.additionalEmails ?? [] : [],
        });
        setEmailValidationErrors(null);
    };

    const isAdditionalEmailsEnabled = data.includeAdditionalEmails ?? false;

    return (
        <div className="px-1">
            <Text className="hs-fs-14 hs-color-BlackRussian hs-fw-500">Send Email to</Text>
            <div className="px-1 d-flex flex-column gap-3 mt-2">
                <Checkbox
                    className="schedule-check-box hs-fs-12"
                    checked={data.recordOwner ?? false}
                    onChange={handleRecordOwnerChange}
                >
                    Record Owner
                </Checkbox>
                <Checkbox
                    className="schedule-check-box hs-fs-12"
                    checked={data.accountOwner ?? false}
                    onChange={handleAccountOwnerChange}
                >
                    Account Owner ({appProperties?.extensionInstalledUserEmailId || ""})
                </Checkbox>
                <Checkbox
                    className="schedule-check-box hs-fs-12"
                    checked={isAdditionalEmailsEnabled}
                    onChange={handleAdditionalEmailsChange}
                >
                    Additional Email Addresses
                </Checkbox>
            </div>
            {isAdditionalEmailsEnabled && (
                <div className="px-1 py-3 m-1 mt-3 mt-sm-2">
                    <EmailComponent
                        parentKey="workflowNotify"
                        emailList={data.additionalEmails ?? []}
                        field={emailFieldConfig}
                        setErrorObj={setEmailValidationErrors}
                    />
                    {emailValidationErrors?.["Email notification"] && (
                        <div className="hs-fs-12 hs-color-red mt-1">{emailValidationErrors["Email notification"]}</div>
                    )}
                </div>
            )}
            <div className="px-1 py-3">
                <Text className="trigger-rule-config-panel-label hs-fw-500 hs-fs-12 hs-color-BlackRussian">
                    Message
                </Text>
                <HTextArea
                    value={data.notifyMessage ?? DEFAULT_EMAIL_NOTIFICATION_MESSAGE}
                    readOnly
                    className="trigger-rule-config-panel-textarea w-100"
                    rows={4}
                    autoSize={{ minRows: 4 }}
                />
            </div>
        </div>
    );
};

/** Keys aligned with settingConfig.js loggingPreference */
const ADD_NOTE_FORM_FIELDS = [
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const AddNoteForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 py-3">
        {ADD_NOTE_FORM_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                        className: "schedule-card-group",
                    }}
                    radioProps={{ className: "schedule-card" }}
                />
            </div>
        ))}
    </div>
);

/** Keys aligned with settingConfig.js loggingPreference */
const LOG_ACTIVITY_FORM_FIELDS = [
    {
        key: "logRecordAsThread",
        label: "Merge all activities into a single activity as notes?",
        options: [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
        ],
    },
    {
        key: "logRecordsFor",
        label: "Where would you prefer to log received message?",
        options: [
            { label: "All matching records", value: "all" },
            { label: "Recent record", value: "recent" },
        ],
    },
];

const LogActivityForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 py-3">
        {LOG_ACTIVITY_FORM_FIELDS.map((field) => (
            <div key={field.key} className="mb-3">
                <HRadioGroup
                    groupLabel={field.label}
                    options={field.options}
                    radioGroupProps={{
                        value: data[field.key as keyof ActionNodeData] ?? field.options[0].value,
                        onChange: (e: { target: { value: string } }) =>
                            update({ [field.key]: e.target.value } as Partial<ActionNodeData>),
                        className: "schedule-card-group",
                    }}
                    radioProps={{ className: "schedule-card" }}
                />
            </div>
        ))}
    </div>
);

const CREATE_LEAD_DESCRIPTION =
    "Enable this option to automatically create a new lead in your CRM whenever a message or call is received from a number that isn't linked to any existing contact or lead.";

const DELAY_TIME_FIELDS = [
    { key: "delayDays", min: 0, max: 30, label: "Days" },
    { key: "delayHours", min: 0, max: 23, label: "Hours" },
    { key: "delayMinutes", min: 0, max: 59, label: "Minutes" },
];

const DelayForm: React.FC<{ data: ActionNodeData; update: (d: Partial<ActionNodeData>) => void }> = ({
    data,
    update,
}) => (
    <div className="px-1 py-3">
        <Text className="trigger-rule-config-panel-label hs-fw-500 hs-fs-12 hs-color-BlackRussian mb-2">
            Wait for
        </Text>
        <Text className="trigger-rule-config-panel-hint hs-fs-12 hs-color-mediumDark d-block mb-2">
            Delay must be between 15 minutes and 30 days.
        </Text>
        <TimeSelector
            value={{
                delayDays: data.delayDays ?? 0,
                delayHours: data.delayHours ?? 0,
                delayMinutes: data.delayMinutes ?? 15,
            }}
            onChange={(val: { delayDays: number; delayHours: number; delayMinutes: number }) =>
                update({
                    delayDays: val.delayDays ?? 0,
                    delayHours: val.delayHours ?? 0,
                    delayMinutes: val.delayMinutes ?? 15,
                })
            }
            fields={DELAY_TIME_FIELDS}
        />
    </div>
);

const CreateLeadForm: React.FC = () => (
    <div className="px-1 py-3">
        <Text className="hs-fs-12 hs-color-dark-grey">{CREATE_LEAD_DESCRIPTION}</Text>
    </div>
);

function renderActionFormContent(
    actionTypeOrCode: string,
    data: ActionNodeData,
    update: (d: Partial<ActionNodeData>) => void
): React.ReactNode {
    const formType = getFormTypeForAction(actionTypeOrCode);
    switch (formType) {
        case "AutomatedResponse":
            return <AutomatedResponseForm data={data} update={update} />;
        case "NotifyUser":
            return <NotifyUserForm data={data} update={update} />;
        case "CreateLead":
            return <CreateLeadForm />;
        case "Delay":
            return <DelayForm data={data} update={update} />;
        case "AddNote":
            return <AddNoteForm data={data} update={update} />;
        case "LogActivity":
            return <LogActivityForm data={data} update={update} />;
        default:
            return (
                <div className="px-1 py-3">
                    <Text className="trigger-rule-config-panel-hint hs-fs-12 hs-color-mediumDark">
                        Configuration for this action type is not yet available.
                    </Text>
                </div>
            );
    }
}

export const ActionConfigPanel: React.FC<ActionConfigPanelProps> = ({ actionTypes }) => {
    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const selectNode = useWorkflowStore((s) => s.selectNode);
    const triggerType = useWorkflowStore((s) => s.triggerType);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo(
        () => ((selectedStep?.data as ActionNodeData) ?? {}),
        [selectedStep?.data]
    );

    const { draftData, updateDraft, commitDraft, hasDraftChanges, setInitialData } = useStepConfigPanel<ActionNodeData>(
        nodeId,
        persistedData,
        updateStepData
    );

    const [showHelp, setShowHelp] = useState(false);
    const prevNodeIdRef = useRef(nodeId);

    useEffect(() => {
        if (prevNodeIdRef.current !== nodeId) {
            prevNodeIdRef.current = nodeId;
            setShowHelp(false);
        }
    }, [nodeId]);

    const isPlaceholderStep =
        (persistedData.isPlaceholder ?? false) || (!persistedData.actionType && !persistedData.actionCode);
    const canCommit = Boolean(draftData.actionCode || draftData.actionType);
    const canAddStep = canCommit;
    const canSaveChanges = hasDraftChanges;

    const handleClose = () => selectNode(null);
    const showPicker =
        !persistedData.actionType &&
        !persistedData.actionCode &&
        !draftData.actionType &&
        !draftData.actionCode;

    const handleSelectActionType = (code: string, title: string) => {
        const formType = getFormTypeForAction(code);
        const initialPartial: Partial<ActionNodeData> = {
            actionCode: code,
            actionType: formType,
            title,
            isPlaceholder: false,
        };
        if (formType === "AddNote" || formType === "LogActivity") {
            initialPartial.logRecordAsThread = "false";
            initialPartial.logRecordsFor = "all";
        }
        if (formType === "Delay") {
            initialPartial.delayDays = 0;
            initialPartial.delayHours = 0;
            initialPartial.delayMinutes = 15;
        }
        updateDraft(initialPartial);
    };

    const handlePrimaryClick = () => {
        if (isPlaceholderStep) {
            if (!canAddStep || selectedStepIndex < 0 || !selectedStep || !nodeId) return;
            const dataToCommit = { ...draftData, isPlaceholder: false };
            updateStepData(nodeId, dataToCommit);
            setInitialData(dataToCommit);
            return;
        }
        if (!canSaveChanges) return;
        commitDraft();
    };

    if (showPicker) {
        return (
            <ConfigPanelShell title="Actions" onClose={handleClose}>
                <StepTypePicker
                    items={actionTypes}
                    onSelect={handleSelectActionType}
                    getIcon={(code) => getActionIconAndClass(code).icon}
                />
            </ConfigPanelShell>
        );
    }

    const displayTitle = draftData.title ?? draftData.actionType ?? draftData.actionCode;
    const formType = getFormTypeForAction(draftData.actionCode ?? draftData.actionType ?? "");
    const helpContent = ACTION_HELP_CONTENT[formType] ?? ACTION_HELP_CONTENT.default;

    return (
        <ConfigPanelShell
            title={displayTitle ?? "Action"}
            onClose={handleClose}
            showHelp={showHelp}
            helpContent={helpContent}
            onHelpToggle={() => setShowHelp((prev) => !prev)}
            footer={
                <ConfigPanelFooter
                    primaryLabel={isPlaceholderStep ? "Apply" : "Save Changes"}
                    primaryDisabled={isPlaceholderStep ? !canAddStep : !canSaveChanges}
                    onPrimaryClick={handlePrimaryClick}
                    hideDelete={triggerType === Trigger.Message.Outbound}
                />
            }
        >
            {renderActionFormContent(
                draftData.actionCode ?? draftData.actionType ?? "",
                draftData,
                updateDraft
            )}
        </ConfigPanelShell>
    );
};
